import {
  faBrain,
  faGlobe,
  faGraduationCap,
  faHeader,
  faKey,
  faListUl,
  faNewspaper,
  faPenClip,
  faSave,
  faSignOut,
  faTrash,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useApiCall from 'contexts/ApiCall';
import { useGradebook } from 'contexts/TeacherGradebookContext';
import Classroom from 'models/Classroom';
import Module from 'models/Module';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { moduleService } from 'services/moduleService';
import teacherService from 'services/teacherService';
import { LearnerLevel, Topic } from 'utils/types';
import ActionButton from 'view/components/buttons/ActionButton';
import FinePrint from 'view/components/common/FinePrint';
import WordAdder from 'view/components/common/WordAdder';

type ModuleFormProps = {
  module?: Module;
  classroom: Classroom | null;
  onExit: (module: Module) => void;
};

type ModuleFormData = {
  name: string;
  classroom: string;
  start_date?: string;
  due_date?: string;
  learner_level?: string[];
  is_public: boolean;
  is_under_review: boolean;
  tags?: string[];
  topics?: string[];
  description: string;
  key_words?: string[];
  advanced_words?: string[];
};

const initialFormData = {
  name: 'My Module',
  classroom: '',
  description: '',
  is_public: false,
  is_under_review: false,
};

const ModuleForm: React.FC<ModuleFormProps> = ({
  module,
  classroom,
  onExit,
}) => {
  const [formData, setFormData] = useState<ModuleFormData>(
    module ? (module as ModuleFormData) : initialFormData,
  );
  const { setGradebook } = useGradebook();
  const makeApiCall = useApiCall();
  const navigate = useNavigate();

  useEffect(() => {
    if (!classroom) return;
    setFormData((prev: ModuleFormData) => {
      return {
        ...prev,
        classroom: classroom.id,
      };
    });
  }, [classroom]);

  const handleSubmitModule = () => {
    if (!module?.id || module.id === '') {
      makeApiCall(teacherService.createModule, formData)
        .then((resp) => {
          setGradebook((prev) => prev?.addModule(resp));
          onExit(Module.fromServerModule(resp));
        })
        .catch((err) => alert(err));
    } else {
      makeApiCall(teacherService.updateModule, module.id, formData)
        .then((resp) => {
          setGradebook((prev) => prev?.updateModule(resp));
          onExit(Module.fromServerModule(resp));
        })
        .catch((err) => alert(err));
    }
  };

  const handleDeleteModule = () => {
    if (!module?.id || module.id === '') return;
    makeApiCall(teacherService.deleteModule, module.id)
      .then((resp) => {
        setGradebook((prev) => prev?.removeModule(module));
        onExit(Module.fromServerModule(resp));
        navigate('..');
      })
      .catch((err) => alert(err));
  };

  const updateModule = (event: any) => {
    if (!formData || !classroom) return;
    var value = event.target.value;
    setFormData((prev: any) => {
      return {
        ...prev,
        [event.target.id]: value,
      };
    });
  };

  const handleUpdateSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (value === '') {
      updateModule({ target: { value: [], id: event.target.id } });
    } else {
      updateModule({ target: { value: [value], id: event.target.id } });
    }
  };

  const handleGenerateModule = () => {
    makeApiCall(moduleService.generateModule, formData)
      .then((resp) => onExit(resp))
      .catch((error) => alert(error));
  };

  return (
    <div className="form">
      <label className="label-large">Module details</label>
      <div className="assignment-attribute-container">
        <div className="row">
          <FontAwesomeIcon icon={faHeader} />
          <label className="label-normal">Name</label>
        </div>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={updateModule}
        />
      </div>
      <div className="assignment-attribute-container">
        <div className="row">
          <FontAwesomeIcon icon={faPenClip} />
          <label className="label-normal">Description</label>
        </div>
        <input
          type="text"
          id="description"
          value={formData.description}
          onChange={updateModule}
        />
      </div>
      <div className="assignment-attribute-container">
        <div className="row">
          <FontAwesomeIcon icon={faNewspaper} />
          <label className="label-normal">Topic</label>
        </div>
        <select
          id="topics"
          value={formData.topics?.length ? formData.topics[0] : ''}
          onChange={handleUpdateSelect}
        >
          <option value="">None</option>
          {Object.values(Topic)
            .sort()
            .map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
        </select>
      </div>
      <div className="assignment-attribute-container">
        <div className="row">
          <FontAwesomeIcon icon={faListUl} />
          <label className="label-normal">Tags</label>
        </div>
        <WordAdder
          words={formData.tags ?? []}
          onAddWord={(word: string) =>
            setFormData((prev) => {
              return { ...prev, tags: [...(prev.tags ?? []), word] };
            })
          }
          onDeleteWord={(word: string) =>
            setFormData((prev) => {
              return {
                ...prev,
                tags: prev.tags!.filter((w) => w !== word),
              };
            })
          }
        />
        <FinePrint text="e.g. Cats, WWII, Conjunctions, etc." />
      </div>
      <div className="assignment-attribute-container">
        <div className="row">
          <FontAwesomeIcon icon={faGraduationCap} />
          <label className="label-normal">Learner Level</label>
        </div>
        <select
          id="learner_level"
          value={
            formData.learner_level?.length ? formData.learner_level[0] : ''
          }
          onChange={handleUpdateSelect}
        >
          <option value="">None</option>
          {Object.values(LearnerLevel).map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </select>
      </div>
      <div className="assignment-attribute-container">
        <div className="row">
          <FontAwesomeIcon icon={faKey} />
          <label className="label-normal">Key words</label>
        </div>
        <WordAdder
          words={formData.key_words ?? []}
          onAddWord={(word: string) =>
            setFormData((prev) => {
              return { ...prev, key_words: [...(prev.key_words ?? []), word] };
            })
          }
          onDeleteWord={(word: string) =>
            setFormData((prev) => {
              return {
                ...prev,
                key_words: prev.key_words!.filter((w) => w !== word),
              };
            })
          }
          generatePrompt={`10 basic, simple, easy, common vocabulary words for a ${formData.learner_level} English language learner about ${formData.name} (${formData.description})`}
        />
        <FinePrint text="Important, common words for this module" />
      </div>
      <div className="assignment-attribute-container">
        <div className="row">
          <FontAwesomeIcon icon={faBrain} />
          <label className="label-normal">Advanced words</label>
        </div>
        <WordAdder
          words={formData.advanced_words ?? []}
          onAddWord={(word: string) =>
            setFormData((prev) => {
              return {
                ...prev,
                advanced_words: [...(prev.advanced_words ?? []), word],
              };
            })
          }
          onDeleteWord={(word: string) =>
            setFormData((prev) => {
              return {
                ...prev,
                advanced_words: prev.advanced_words!.filter((w) => w !== word),
              };
            })
          }
          generatePrompt={`8 advanced vocabulary words for a ${formData.learner_level} English language learner about ${formData.name} (${formData.description})`}
        />
        <FinePrint text="Advanced words for this module" />
      </div>
      <div className="assignment-attribute-container">
        <div className="row">
          <FontAwesomeIcon icon={faGlobe} />
          <label className="label-normal">Make Public</label>
          <input
            type="checkbox"
            id="is_public"
            checked={formData?.is_public || formData?.is_under_review}
            onChange={(event) => {
              if (formData?.is_public) {
                updateModule({
                  target: { id: 'is_public', value: !formData?.is_public },
                });
              } else {
                updateModule({
                  target: {
                    id: 'is_under_review',
                    value: event.target.checked,
                  },
                });
              }
            }}
          />
        </div>
        <FinePrint text="This enables your submission to be viewable in our public catalog" />
      </div>
      <div className="row">
        {(!module?.id || module.id === '') && (
          <ActionButton
            type="edit"
            label="Generate"
            onClick={handleGenerateModule}
            icon={faWandMagicSparkles}
          />
        )}
        {module?.id && module.id !== '' ? (
          <ActionButton
            type="delete"
            label="Delete"
            requiresConfirmation={true}
            confirmationMessage="Are you sure you want to delete this module? All associated assignments and submissions will be lost."
            onClick={handleDeleteModule}
            icon={faTrash}
          />
        ) : (
          <ActionButton
            type="delete"
            label="Cancel"
            onClick={() => onExit(module as Module)}
            icon={faSignOut}
          />
        )}
        <ActionButton
          type="go"
          label="Submit"
          onClick={handleSubmitModule}
          icon={faSave}
        />
      </div>
    </div>
  );
};

export default ModuleForm;
